import React, { PropsWithChildren, useMemo } from 'react';
import PropTypes from 'prop-types';

import { envType } from '@atlassiansox/analytics-web-client';
import {
	EngagementProvider as EngagementProviderWithoutConfig,
	ENABLE_CHOREOGRAPHER_GATE,
} from '@atlassiansox/engagekit-ts';

import { getAnalyticsWebClient, getWorkspaceId } from '@confluence/analytics-web-client';
import {
	type SessionDataType,
	useSessionData,
	useBooleanFeatureFlag,
} from '@confluence/session-data';

const getAnalyticsEnv = (environment: SessionDataType['environment']) => {
	return window.location.hostname === 'localhost'
		? envType.DEV
		: environment === 'STAGING'
			? envType.STAGING
			: environment === 'PRODUCTION'
				? envType.PROD
				: envType.DEV;
};

export function EngagementProviderComponent(props: PropsWithChildren<{}>) {
	const { cloudId, locale, userId, featureFlags, activationId, environment } = useSessionData();

	const workspaceId = getWorkspaceId(cloudId, activationId);

	if (
		!featureFlags['confluence.frontend.engage-platform.engagekit'] ||
		sessionStorage.getItem('confluence.disable-engagement-for-tests') === 'true'
	) {
		return <>{props.children}</>;
	}

	const config = {
		cloudId,
		aaid: userId as string,
		locale,
		product: 'confluence',
		stargateUrl: '/gateway/api',
		workspaceId,
		isFedRAMPEnvironment: false,
	};

	const enableChoreographer = useBooleanFeatureFlag(ENABLE_CHOREOGRAPHER_GATE.confluence);
	const coordinationOptions = useMemo(
		() => ({
			enableChoreographer,
			envType: getAnalyticsEnv(environment),
		}),
		[enableChoreographer, environment],
	);

	return (
		<EngagementProviderWithoutConfig
			{...props}
			children={<>{props.children}</>}
			config={config}
			analyticsClientInstance={getAnalyticsWebClient()}
			coordinationOptions={coordinationOptions}
		/>
	);
}

EngagementProviderComponent.propTypes = {
	children: PropTypes.element,
	locale: PropTypes.string,
};
